import { Checkbox, Modal } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { DoctorProfile, SnackingQuestion } from '../api/getSnackingQuestions';
import { AntTooltip } from '../components/common/AntTooltip';
import { apiDrugToMedicationEntity } from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import { SNACKING_QUESTIONS_NEW } from '../components/prescriptionPad/trackSource';
import { GO_TO_MY_GENIE } from '../routes/paths';
import { Actions } from '../store/actions';
import { answerSnackingQuestion } from '../store/actions/session';
import { GlobalState } from '../store/reducers';
import { MedicationsEntity } from '../store/types/prescription';
import { track } from '../tracking';
import { PARCHI_SNACKING_QUESTIONS_VERSION } from './SnackingQuestionsComponent';

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    isModalVisible: boolean;
    hideModalHandler: () => void;
    snackingQuestion: SnackingQuestion[];
    doctorProfile?: DoctorProfile;
    onSuccess: () => void;
}

type State = {
    questions: SnackingQuestion[];
    isKeepNameChecked: boolean;
    removedMeds: Set<string>;
    isAcceptChangeChecked: boolean;
};

class SnackingQuestionsModal extends PureComponent<Props, State> {
    state: State = {
        questions: [],
        isKeepNameChecked: false,
        removedMeds: new Set(),
        isAcceptChangeChecked: false,
    };

    componentDidUpdate = (prevProps: Props) => {
        const { snackingQuestion } = this.props;
        if (prevProps.snackingQuestion !== snackingQuestion) {
            this.setState({
                questions: snackingQuestion,
            });
        }
    };

    confirmHandler = (): void => {
        const { snackingQuestion, dispatch, hideModalHandler, onSuccess } = this.props;
        const { isKeepNameChecked, removedMeds } = this.state;
        const newDrugs: MedicationsEntity[] = [];

        snackingQuestion.forEach((i) => {
            const { med: drug, suggestions } = i;
            const med = suggestions[0];
            const apiToMed = apiDrugToMedicationEntity(med);
            const newDrug = {
                ...apiToMed,
                ...drug,
                id: med.id,
                name: med.name,
                form: '',
                dosage_form: med.dosage?.dosage_form || '',
                df_id: med.dosage?.df_id,
                generic_id: med.generic_id,
                generic_name: med.generic_name,
                product_type: med.product_type,
                soa: med.soa || false,
                manufacturer_name: med.manufacturer_name,
                hxng_mapped: med.hxng_mapped,
                hxng_status: med.hxng_status,
                replaced_from: drug,
                replaced_track: {
                    label: SNACKING_QUESTIONS_NEW,
                },
            };

            if (isKeepNameChecked) {
                newDrug.name = drug.name;
                newDrug.actual_name = med.name;
            }

            if (drug.df_id === med.dosage.df_id) {
                newDrug.dose = drug.dose;
            } else {
                newDrug.dose = apiToMed.dose;
            }
            if (removedMeds.has(drug.id)) {
                newDrugs.push({ ...drug, suggestions } as MedicationsEntity);
            } else {
                newDrugs.push(newDrug);
            }
        });
        dispatch(
            answerSnackingQuestion(
                newDrugs,
                undefined,
                undefined,
                undefined,
                true,
                undefined,
                localStorage.getItem(PARCHI_SNACKING_QUESTIONS_VERSION) || '',
            ),
        );
        hideModalHandler();
        onSuccess();
    };

    onKeepNameChange = () => {
        const { isKeepNameChecked } = this.state;
        this.setState({
            isKeepNameChecked: !isKeepNameChecked,
        });
        track('KEEP_NAME_CHECKBOX', { action: isKeepNameChecked ? 'uncheck' : 'check' });
    };

    onAcceptChangeCheckedChange = () => {
        const { isAcceptChangeChecked } = this.state;
        this.setState({
            isAcceptChangeChecked: !isAcceptChangeChecked,
        });
        track('ACCEPT_CHANGE_CHECKBOX', { action: isAcceptChangeChecked ? 'uncheck' : 'check' });
    };

    goToGenieDashboardHandler = () => {
        const { history } = this.props;
        history.push(GO_TO_MY_GENIE('genie-configuration', 'medicines', 'uncoded'));
        track('SNACKING_QUESTIONS_NEW', { action: 'VIEW_ALL' });
    };

    removeMedHandler = (id: string) => {
        const { questions, removedMeds } = this.state;
        removedMeds.add(id);
        this.setState({
            removedMeds,
            questions: questions.filter((i) => i.med.id !== id),
        });
    };

    render(): ReactNode {
        const { isModalVisible, hideModalHandler, doctorProfile } = this.props;
        const { questions, isKeepNameChecked, isAcceptChangeChecked } = this.state;
        const { confirmHandler, goToGenieDashboardHandler, removeMedHandler } = this;

        return (
            <Modal
                title={<p className="font-700">Message from Eka Medico-legal team</p>}
                visible={isModalVisible}
                onOk={hideModalHandler}
                onCancel={hideModalHandler}
                destroyOnClose
                width={1000}
                okText="Done"
                bodyStyle={{
                    padding: '0px',
                    height: '450px',
                }}
                className="space-y-16"
                cancelButtonProps={{ style: { display: 'none' } }}
                closeIcon={
                    <div className="flex items-center justify-center h-full">
                        <svg
                            className="w-14 h-14 fill-text-secondary"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                        </svg>
                    </div>
                }
                footer={
                    <div className="flex items-center justify-between px-8">
                        <button
                            className="text-darwin-primary-500 font-700"
                            onClick={goToGenieDashboardHandler}
                        >
                            View all
                        </button>

                        <div className="flex items-center space-x-16">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    onChange={this.onAcceptChangeCheckedChange}
                                    value={isAcceptChangeChecked}
                                    className="text-13 font-400"
                                >
                                    I accept the above changes
                                </Checkbox>
                                <AntTooltip content="Kindly understand that Eka offers guidance and support to aid doctors in compliance, but full responsibility for adhering to NMC guidelines rests with the individual doctor.">
                                    <div className="pt-3">
                                        <svg
                                            className="w-16 h-16 fill-darwin-neutral-600"
                                            viewBox="0 0 12 13"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M6 12.5C2.67188 12.5 0 9.82812 0 6.5C0 3.19531 2.67188 0.5 6 0.5C9.30469 0.5 12 3.19531 12 6.5C12 9.82812 9.30469 12.5 6 12.5ZM5.0625 8.375C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375H6.75V6.3125C6.75 6.00781 6.49219 5.75 6.1875 5.75H5.0625C4.73438 5.75 4.5 6.00781 4.5 6.3125C4.5 6.64062 4.73438 6.875 5.0625 6.875H5.625V8.375H5.0625ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z" />
                                        </svg>
                                    </div>
                                </AntTooltip>
                            </div>
                            <button
                                className={`px-16 py-8 bg-darwin-primary-500 text-white rounded-4 font-700 ${
                                    !isAcceptChangeChecked ? 'opacity-70' : ''
                                }`}
                                onClick={confirmHandler}
                                disabled={!isAcceptChangeChecked}
                            >
                                Confirm All
                            </button>
                        </div>
                    </div>
                }
                style={{ top: 50, height: 500 }}
            >
                <div className="h-full w-full flex items-start space-x-16 p-16">
                    {doctorProfile && (
                        <div className="p-16 bg-darwin-neutral-50 rounded-16 overflow-scroll">
                            <p className="text-13 text-darwin-neutral-900 font-700 uppercase">
                                Meet our expert
                            </p>
                            <div className="flex items-start bg-white mt-16 mb-24 rounded-8 overflow-hidden shadow-700">
                                <div className="bg-darwin-accent-dark-blue-800 pt-5">
                                    <img
                                        src={doctorProfile.img}
                                        alt="doctor image"
                                        className="w-116 h-208"
                                    />
                                </div>
                                <div className="p-16 pr-6">
                                    <p className="text-18 font-700">{doctorProfile.name}</p>
                                    <p className="text-12 text-darwin-neutral-700 w-176 mt-10 mb-14">
                                        {doctorProfile.speciality}
                                    </p>
                                    <div className="flex items-start space-x-8 mb-16">
                                        <div className="pt-2">
                                            <svg
                                                className="w-14 h-14 fill-darwin-neutral-900"
                                                viewBox="0 0 14 14"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M3.5 3.375V2.28125C3.5 1.46094 4.18359 0.75 5.03125 0.75H8.96875C9.78906 0.75 10.5 1.46094 10.5 2.28125V3.375H12.25C13.207 3.375 14 4.16797 14 5.125V12.125C14 13.1094 13.207 13.875 12.25 13.875H1.75C0.765625 13.875 0 13.1094 0 12.125V5.125C0 4.16797 0.765625 3.375 1.75 3.375H3.5ZM4.8125 3.375H9.1875V2.28125C9.1875 2.17188 9.07812 2.0625 8.96875 2.0625H5.03125C4.89453 2.0625 4.8125 2.17188 4.8125 2.28125V3.375ZM3.9375 4.6875V12.5625H10.0625V4.6875H3.9375ZM1.3125 5.125V12.125C1.3125 12.3711 1.50391 12.5625 1.75 12.5625H2.625V4.6875H1.75C1.50391 4.6875 1.3125 4.90625 1.3125 5.125ZM12.6875 12.125V5.125C12.6875 4.90625 12.4688 4.6875 12.25 4.6875H11.375V12.5625H12.25C12.4688 12.5625 12.6875 12.3711 12.6875 12.125Z" />
                                            </svg>
                                        </div>
                                        <p className="text-13 text-darwin-neutral-900">
                                            Experience :{' '}
                                            <span className="font-700">
                                                {doctorProfile.experience}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="flex items-start space-x-8">
                                        <div className="pt-2">
                                            <svg
                                                className="w-14 h-15 fill-darwin-neutral-900"
                                                viewBox="0 0 14 15"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M8.3125 9.0625C10.9648 9.0625 13.125 11.2227 13.125 13.875C13.125 14.3672 12.7148 14.75 12.25 14.75H1.75C1.25781 14.75 0.875 14.3672 0.875 13.875C0.875 11.2227 3.00781 9.0625 5.6875 9.0625H8.3125ZM2.1875 13.4375H11.7852C11.5664 11.7148 10.0898 10.375 8.3125 10.375H5.6875C3.88281 10.375 2.40625 11.7148 2.1875 13.4375ZM7 7.75C5.05859 7.75 3.5 6.19141 3.5 4.25C3.5 2.33594 5.05859 0.75 7 0.75C8.91406 0.75 10.5 2.33594 10.5 4.25C10.5 6.19141 8.91406 7.75 7 7.75ZM7 2.0625C5.76953 2.0625 4.8125 3.04688 4.8125 4.25C4.8125 5.48047 5.76953 6.4375 7 6.4375C8.20312 6.4375 9.1875 5.48047 9.1875 4.25C9.1875 3.04688 8.20312 2.0625 7 2.0625Z" />
                                            </svg>
                                        </div>
                                        <p className="text-13 text-darwin-neutral-900 font-700">
                                            {doctorProfile.title}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-y-24">
                                <div className="space-y-6">
                                    <p className="text-24 font-700">
                                        {doctorProfile.drugsReviewed}
                                    </p>
                                    <p className="text-13 text-darwin-neutral-800">
                                        Total drugs reviewed
                                    </p>
                                </div>
                                <div className="space-y-6">
                                    <p className="text-24 font-700 text-darwin-accent-sulphur-green-500">
                                        {doctorProfile.acceptanceRate}
                                    </p>
                                    <p className="text-13 text-darwin-neutral-800">
                                        Drug acceptance rate
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="space-y-16 h-full flex-1 overflow-scroll">
                        <p>
                            Our medico-legal team has made some changes to make sure you’re
                            compliant with{' '}
                            <span className="font-700">ever evolving NMC Guidelines.</span>
                        </p>
                        <div className="p-8 flex items-center space-x-16 text-13 rounded-8 bg-darwin-accent-green-100">
                            <p>Other benefits include :</p>
                            <div className="flex items-center space-x-6">
                                <svg
                                    className="w-16 h-16 fill-darwin-accent-green-800"
                                    viewBox="0 0 16 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M8 14.5C4.67188 14.5 2 11.8281 2 8.5C2 5.19531 4.67188 2.5 8 2.5C11.3047 2.5 14 5.19531 14 8.5C14 11.8281 11.3047 14.5 8 14.5ZM10.6484 7.39844H10.625C10.8594 7.1875 10.8594 6.83594 10.625 6.60156C10.4141 6.39062 10.0625 6.39062 9.85156 6.60156L7.25 9.22656L6.14844 8.125C5.91406 7.89062 5.5625 7.89062 5.35156 8.125C5.11719 8.33594 5.11719 8.6875 5.35156 8.89844L6.85156 10.3984C7.0625 10.6328 7.41406 10.6328 7.64844 10.3984L10.6484 7.39844Z" />
                                </svg>

                                <p>Contra-indication support</p>
                            </div>
                            <div className="flex items-center space-x-6">
                                <svg
                                    className="w-16 h-16 fill-darwin-accent-green-800"
                                    viewBox="0 0 16 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M8 14.5C4.67188 14.5 2 11.8281 2 8.5C2 5.19531 4.67188 2.5 8 2.5C11.3047 2.5 14 5.19531 14 8.5C14 11.8281 11.3047 14.5 8 14.5ZM10.6484 7.39844H10.625C10.8594 7.1875 10.8594 6.83594 10.625 6.60156C10.4141 6.39062 10.0625 6.39062 9.85156 6.60156L7.25 9.22656L6.14844 8.125C5.91406 7.89062 5.5625 7.89062 5.35156 8.125C5.11719 8.33594 5.11719 8.6875 5.35156 8.89844L6.85156 10.3984C7.0625 10.6328 7.41406 10.6328 7.64844 10.3984L10.6484 7.39844Z" />
                                </svg>

                                <p>Manufacturer analytics</p>
                            </div>
                        </div>
                        <div className="border-r border-b border-l border-darwin-neutral-100">
                            <div className="border-t border-darwin-neutral-100 grid grid-cols-2 gap-16 p-8 bg-darwin-neutral-50 text-darwin-neutral-800 font-700 sticky top-0">
                                <p className="font-700">Current Info ({questions.length})</p>
                                <div className="flex items-center space-x-16">
                                    <p>Updated Info</p>
                                    <div className="flex items-center space-x-8">
                                        <Checkbox
                                            onChange={this.onKeepNameChange}
                                            value={isKeepNameChecked}
                                            className="text-13 font-400"
                                        >
                                            Keep current drug name
                                        </Checkbox>
                                    </div>
                                </div>
                            </div>
                            {questions.map((i, index) => (
                                <div
                                    className={`grid grid-cols-2 gap-20 px-8 py-16 border-darwin-neutral-100 ${
                                        index + 1 !== questions.length ? 'border-b' : ''
                                    }`}
                                >
                                    <div className="flex items-center space-x-8">
                                        <div className="flex-1 space-y-6">
                                            <div className="flex items-center space-x-8">
                                                <p>{i.med.name}</p>
                                                {(i.med.dosage_form || i.med.form)?.trim() && (
                                                    <p className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-12 bg-white">
                                                        {i.med.dosage_form || i.med.form}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="text-13 text-darwin-neutral-700">
                                                {i.med.generic_name}
                                            </p>
                                        </div>
                                        <div>
                                            <svg
                                                className="w-15 h-14 cursor-pointer"
                                                viewBox="0 0 15 14"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M14.0209 6.3125C14.4271 6.6875 14.4271 7.34375 14.0209 7.71875L9.02087 12.7188C8.73962 13 8.30212 13.0938 7.92712 12.9375C7.55212 12.7812 7.30212 12.4062 7.30212 12V9H1.58337C0.864624 9 0.333374 8.46875 0.333374 7.75V6.25C0.333374 5.5625 0.864624 5 1.58337 5H7.33337V2C7.33337 1.625 7.55212 1.25 7.92712 1.09375C8.30212 0.9375 8.73962 1.03125 9.02087 1.3125L14.0209 6.3125Z"
                                                    fill="#A3A8C2"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-8">
                                        <div className="flex-1 space-y-5">
                                            <div className="flex items-center space-x-8">
                                                <p>{i.suggestions[0]?.name}</p>
                                                {i.suggestions[0]?.dosage?.dosage_form?.trim() && (
                                                    <p className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-12 bg-white">
                                                        {i.suggestions[0]?.dosage?.dosage_form}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="text-13 text-darwin-neutral-700">
                                                {i.suggestions[0]?.generic_name}
                                            </p>
                                            {i.suggestions[0]?.manufacturer_name && (
                                                <p className="text-13 text-darwin-neutral-600 truncate-overflow-1">
                                                    Manufactured by{' '}
                                                    {i.suggestions[0].manufacturer_name}
                                                </p>
                                            )}
                                        </div>
                                        <AntTooltip content="Don’t change">
                                            <div>
                                                <svg
                                                    className="w-24 h-24 cursor-pointer fill-darwin-status-warning-500"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => removeMedHandler(i.med.id)}
                                                >
                                                    <rect
                                                        className="w-24 h-24"
                                                        rx="12"
                                                        fill="#FFF2F1"
                                                    />
                                                    <path d="M16.1562 15.0039C16.4023 15.2773 16.4023 15.6875 16.1562 15.9336C15.8828 16.207 15.4727 16.207 15.2266 15.9336L12 12.6797L8.74609 15.9336C8.47266 16.207 8.0625 16.207 7.81641 15.9336C7.54297 15.6875 7.54297 15.2773 7.81641 15.0039L11.0703 11.75L7.81641 8.49609C7.54297 8.22266 7.54297 7.8125 7.81641 7.56641C8.0625 7.29297 8.47266 7.29297 8.71875 7.56641L12 10.8477L15.2539 7.59375C15.5 7.32031 15.9102 7.32031 16.1562 7.59375C16.4297 7.83984 16.4297 8.25 16.1562 8.52344L12.9023 11.75L16.1562 15.0039Z" />
                                                </svg>
                                            </div>
                                        </AntTooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
}

export default connect()(withRouter(SnackingQuestionsModal));
